import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/common/StatusBadge";
import DocUpload from "@/components/kyc/DocUpload";
import DocPreviewDialog from "@/components/kyc/DocPreviewDialog";
import EmailVerifyNotice from "@/components/dashboard/EmailVerifyNotice";

const EMPTY = { legal_name: "", date_of_birth: "", country: "", address: "" };
const MESSAGES = {
  not_submitted: "Upload your documents and details below, then submit them for review.",
  pending: "Thanks — your documents are under review. We'll email you once a decision is made.",
  approved: "You're verified. Deposits, investing and withdrawals are unlocked.",
  rejected: "Your last submission was not approved. Please review the note, update your documents and resubmit.",
};

export default function Verification() {
  const { checkAuth } = useAuth();
  const [kyc, setKyc] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/kyc");
    setKyc(data);
    if (data.details) setForm((f) => (f.legal_name ? f : { ...EMPTY, ...data.details }));
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!kyc) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  const locked = kyc.status === "pending" || kyc.status === "approved";
  const docOf = (t) => kyc.documents.find((d) => d.doc_type === t);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/kyc/submit", form);
      toast.success("Submitted for review");
      await load();
      checkAuth();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-x max-w-4xl space-y-8 py-12" data-testid="verification-page">
      <div>
        <p className="eyebrow">Account security</p>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Identity verification</h1>
        <p className="mt-3 max-w-2xl text-slate-400">To protect clients and prevent fraud, we verify every investor before deposits, investments or withdrawals are allowed.</p>
      </div>

      {!kyc.email_verified && <EmailVerifyNotice />}

      <div className="panel flex flex-wrap items-center gap-4 p-6">
        <ShieldCheck className="h-6 w-6 text-emerald-400" />
        <div className="flex-1">
          <div className="flex items-center gap-3"><p className="font-medium text-white">Verification status</p><StatusBadge status={kyc.status} testId="kyc-status-badge" /></div>
          <p className="mt-1 text-sm text-slate-400">{MESSAGES[kyc.status]}</p>
          {kyc.status === "rejected" && kyc.note && <p className="mt-2 text-sm text-red-400" data-testid="kyc-rejection-note">Reason: {kyc.note}</p>}
        </div>
      </div>

      <form onSubmit={submit} className="panel space-y-6 p-6 sm:p-8" data-testid="kyc-form">
        <div className="grid gap-5 sm:grid-cols-2">
          {[["legal_name", "Full legal name", "text"], ["date_of_birth", "Date of birth", "date"], ["country", "Country of residence", "text"]].map(([k, l, t]) => (
            <div key={k} className="space-y-2">
              <Label htmlFor={`kyc-${k}`}>{l}</Label>
              <Input id={`kyc-${k}`} type={t} required disabled={locked} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="field" data-testid={`kyc-${k}-input`} />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <Label htmlFor="kyc-address">Residential address</Label>
          <Textarea id="kyc-address" required disabled={locked} rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl border-line bg-surface" data-testid="kyc-address-input" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <DocUpload docType="id" label="Photo ID" hint="Passport, driving licence or national ID card. JPG, PNG, WEBP or PDF up to 10 MB." doc={docOf("id")} disabled={locked} onUploaded={load} onView={setPreview} />
          <DocUpload docType="address" label="Proof of address" hint="Utility bill or bank statement from the last 3 months." doc={docOf("address")} disabled={locked} onUploaded={load} onView={setPreview} />
        </div>
        <p className="flex items-center gap-2 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Documents are stored privately and are only visible to you and the Meridian compliance team.</p>
        {!locked && (
          <button type="submit" disabled={busy || !kyc.email_verified} className="btn-primary w-full sm:w-auto" data-testid="kyc-submit-button">
            {busy ? "Submitting..." : "Submit for review"}
          </button>
        )}
      </form>
      <DocPreviewDialog doc={preview} onOpenChange={(o) => !o && setPreview(null)} />
    </div>
  );
}
